import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { IClinic } from '../../../interfaces/clinic';
import {
  isCohortEnabledForClinic,
  PROGRAM_COHORT_OPTIONS,
} from '../../../lib/referrals/cohorts';
import { Clinic } from '../../../interfaces/eligibility';
import {
  ClinicConfig,
  LeadAndProvider,
  LeadSystemSource,
  Source,
} from '../../../interfaces/onboarding';
import { AuthService } from '../../../services/auth';
import { CommonService } from '../../../services/common';
import eligibilityService from '../../../services/eligibility';
import {
  default as onboardingClient,
  default as onboardingService,
} from '../../../services/onboarding';
import { formatDateMMDDYYYY } from '../../../util/dateFormat';
import { camelCaseToPascalCase } from '../../../util/stringFormat';
import {
  getBMI,
  HEIGHT_FEET_RANGE,
  HEIGHT_INCHES_RANGE,
  isEmailValid,
  isEmpty,
  isHeightFeetValid,
  isHeightInchesValid,
  isPhoneValid,
  isValidDateOfBirth,
  isWeightLbsValid,
  WEIGHT_LBS_RANGE,
} from '../../../util/validations';
import { AVAILABLE_LANGUAGES, AVAILABLE_PLAN_TYPES } from './insurance';
import './scss/referral-form.css';
import { Gender, Option, ReferralFormFields } from './types';

type FormFields = Omit<ReferralFormFields, 'dob' | 'clinicId'> & {
  dob: Date | null;
  clinicId: string;
  isCommunityReferral: boolean;
  communityReferralClinicName: string;
};

type Props = {
  loading: boolean;
  clinic: Clinic | null;
};

const DEFAULT_FORM_FIELDS = {
  agentEmail: '',
  bmi: null,
  city: '',
  clinicId: '',
  dob: null,
  email: '',
  feet: null,
  firstName: '',
  gender: '',
  groupNumber: null,
  inches: null,
  insuranceId: null,
  insuranceName: '',
  insurancePlanType: '',
  lastName: '',
  otherInsuranceName: '',
  outsideMRN: null,
  phone: null,
  preferredLanguage: '',
  referredBy: '',
  source: Source.PartnerReferral,
  state: '',
  street: '',
  systemSource: LeadSystemSource.PartnerReferral,
  weight: null,
  zipCode: '',
  isCommunityReferral: false,
  communityReferralClinicName: '',
  metadata: {
    secondaryInsurance: {
      insuranceId: null,
      insuranceName: '',
      insurancePlanType: '',
      groupNumber: null,
      otherInsuranceName: '',
    },
  },
};

/**
 * Shared by the two selects on the Patient information row. They sit in a flex row with
 * `alignItems: center`, so a field that sizes itself differently ends up both narrower and
 * vertically offset from its neighbour — which is exactly what happened when the cohort
 * picker's helper text was removed and stopped propping its width open.
 */
const ROW_SELECT_SX = {
  width: '280px',
  '& .MuiInputBase-root': {
    height: '2.7em',
  },
  '& .MuiInputBase-input': {
    height: '0.5em',
    fontSize: '16px',
  },
  '& label': {
    top: '-5px',
    fontSize: '15px',
  },
  '& fieldset.MuiOutlinedInput-notchedOutline': {
    borderColor: '#d5d5d5',
  },
} as const;

const REQUIRED_FIELDS: (keyof FormFields)[] = [
  'agentEmail',
  'bmi',
  'clinicId',
  'dob',
  'firstName',
  'insuranceId',
  'insuranceName',
  'lastName',
  'phone',
  'preferredLanguage',
  'referredBy',
  'source',
  'systemSource',
  'zipCode',
];

const CONDITIONAL_REQUIRED_FIELDS: { [key: string]: (keyof FormFields)[] } = {
  feet: ['bmi'],
  inches: ['bmi'],
  weight: ['bmi'],
};

const REQUIRED_MAP = new Map<keyof FormFields, boolean>(REQUIRED_FIELDS.map((f) => [f, true]));

const ReferralForm: FC<Props> = ({ loading }) => {
  const [fields, setFields] = useState<Partial<FormFields>>(DEFAULT_FORM_FIELDS);
  const [error, setError] = useState<
    { [fields in keyof Partial<ReferralFormFields>]: string | null } | null
  >(null);
  const [payerOptions, setPayerOptions] = useState<Option[]>([]);
  const [referringDoctorsOptions, setReferringDoctorsOptions] = useState<Option[]>([]);
  const [clinicConfig, setClinicConfig] = useState<ClinicConfig | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [canEditBMI, setCanEditBMI] = useState<boolean>(false);
  const [clinics, setClinics] = useState<IClinic[]>([]);
  const [currentClinic, setCurrentClinic] = useState<IClinic | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [user, setUser] = useState<{
    id: string;
    fullName: string;
    email: string;
    uuid: string;
  } | null>(null);
  const isFieldRequired = (field: keyof FormFields) => {
    if (REQUIRED_MAP.has(field)) {
      return !!REQUIRED_MAP.get(field);
    }

    const conditions = CONDITIONAL_REQUIRED_FIELDS[field] || null;

    if (conditions === null) {
      return false;
    }

    return !conditions.every((field) => !!fields[field]);
  };

  const validateField = (
    fields: Partial<FormFields>,
    name: keyof FormFields,
    value: any,
  ): boolean => {
    let error: string | null = null;
    const isRequired = (!!!fields?.[name] || !!!value) && isFieldRequired(name);

    if (isRequired) {
      setError((prev) => ({
        ...prev,
        [name]: `${camelCaseToPascalCase(name)} is required`,
      }));
      return false;
    }

    if (isEmpty(value) && !isFieldRequired(name)) {
      setError((prev) => ({
        ...prev,
        [name]: null,
      }));
      return true;
    }

    if (name === ('email' as keyof FormFields) || name === ('agentEmail' as keyof FormFields)) {
      if (!isEmailValid(value) && value) {
        error = 'Please enter a valid email address (e.g., name@example.com)';
      }
    }

    if (name === ('dob' as keyof FormFields)) {
      const { valid, reason } = isValidDateOfBirth(value);
      if (!valid && reason) {
        error = reason;
      }
    }

    if (name === ('phone' as keyof FormFields)) {
      if (!isPhoneValid(String(value)) && value) {
        error = 'Please enter a valid phone number';
      }
    }

    if (name === ('feet' as keyof FormFields)) {
      if (!isHeightFeetValid(String(value)) && value) {
        error = `Height must be between ${HEIGHT_FEET_RANGE.min} and ${HEIGHT_FEET_RANGE.max} feet`;
      }
    }

    if (name === ('inches' as keyof FormFields)) {
      if (!isHeightInchesValid(String(value)) && value) {
        error = `Inches must be between ${HEIGHT_INCHES_RANGE.min} and ${HEIGHT_INCHES_RANGE.max}`;
      }
    }

    if (name === ('weight' as keyof FormFields)) {
      if (!isWeightLbsValid(String(value)) && value) {
        error = `Weight must be between ${WEIGHT_LBS_RANGE.min} and ${WEIGHT_LBS_RANGE.max} lbs`;
      }
    }

    if (name === ('firstName' as keyof FormFields)) {
      if (value && value.length < 2) {
        error = 'First name must be at least 2 characters';
      }
    }

    if (name === ('lastName' as keyof FormFields)) {
      if (value && value.length < 2) {
        error = 'Last name must be at least 2 characters';
      }
    }

    if (name === ('zipCode' as keyof FormFields)) {
      if (value && !/^\d{5}(-\d{4})?$/.test(value)) {
        error = 'Please enter a valid zip code (e.g., 12345)';
      }
    }

    if (name === ('city' as keyof FormFields)) {
      if (value && value.length < 2) {
        error = 'City name must be at least 2 characters';
      }
    }

    if (name === ('insuranceId' as keyof FormFields)) {
      if (value && String(value).length < 3) {
        error = 'Insurance ID must be at least 3 characters';
      }
    }

    setError((prev) => ({
      ...prev,
      [name]: error,
    }));

    return !!!error;
  };

  const handleField = (
    e:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: any } },
    skipValidation: boolean = false,
  ) => {
    const { name, value } = e.target;

    const setNestedField = (path: string, value: any, obj: any = { ...fields }): any => {
      const keys = path.split('.');
      const lastKey = keys.pop() as string;
      const target = keys.reduce((acc, key) => {
        acc[key] = acc[key] ?? {};
        return acc[key];
      }, obj);
      target[lastKey] = value;
      return obj;
    };

    const updatedFields = setNestedField(name, value);

    if (!skipValidation) {
      validateField(updatedFields, name as keyof FormFields, value);
    }

    setFields(updatedFields);
  };

  const handleBlur = (name: keyof FormFields) => {
    const value = fields[name];
    validateField(fields, name, value);
  };

  const handleToggleBmiEdition = () => {
    const canEditBMINow = !canEditBMI;

    if (canEditBMINow) {
      setError({
        ...error,
        feet: null,
        inches: null,
        weight: null,
      });

      setFields({
        ...fields,
        feet: null,
        inches: null,
        weight: null,
      });
    }

    setCanEditBMI(canEditBMINow);
  };

  const fetchPayers = async () => {
    const payers = await eligibilityService.getPayerList({
      page: 1,
      clinicId: Number(currentClinic?.id),
    });

    setPayerOptions(payers.map(({ id, name }) => ({ label: name, value: name })));
  };

  const fetchClinicConfig = async () => {
    const clinicConfig = await onboardingClient.getClinicConfig(Number(currentClinic?.id));

    if (!clinicConfig) {
      console.error('Clinic config can not be loaded');
      return null;
    }

    setClinicConfig(clinicConfig);
    setReferringDoctorsOptions(
      clinicConfig.referringDoctors.map((r) => ({ label: r.name, value: r.name })),
    );

    return clinicConfig;
  };

  const handleClearForm = () => {
    setFields({
      ...DEFAULT_FORM_FIELDS,
      ...(currentClinic?.id && { clinicId: `${currentClinic.id}` }),
      zipCode: currentClinic?.zip_code || '',
      agentEmail: user?.email,
      systemSource: LeadSystemSource.PartnerReferral,
      source: Source.PartnerReferral,
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const isValid = REQUIRED_FIELDS.filter(
      (field) =>
        !validateField(fields, field as keyof FormFields, fields[field as keyof FormFields]),
    );

    if (!(isValid.length === 0)) {
      setIsSubmitting(false);
      return;
    }

    const lead = {
      meta: {
        systemSource: fields.systemSource,
        isCommunityReferral: fields.isCommunityReferral,
        communityReferralClinicName: fields.isCommunityReferral
          ? fields.communityReferralClinicName
          : undefined,
      },
      patient: {
        clinicId: Number(fields.clinicId),
        clinicName: clinicConfig?.name,
        firstName: fields.firstName,
        lastName: fields.lastName,
        email: fields.email,
        referredBy: fields.referredBy,
        source: fields.source,
        dateOfBirth: fields.dob,
        feet: fields.feet,
        weight: fields.weight,
        inches: fields.inches,
        bmi: fields.bmi,
        street: fields.street,
        state: fields.state,
        zipCode: fields.zipCode,
        preferredLanguage: fields.preferredLanguage,
        groupNumber: fields.groupNumber,
        gender: fields.gender,
        otherInsurance: fields.otherInsuranceName,
        insurancePlanType: fields.insurancePlanType,
        insuranceId: fields.insuranceId,
        payer: fields.insuranceName,
        city: fields.city,
        phone: fields.phone,
        metadata: {
          secondaryInsurance: {
            insurancePlanType: fields.metadata?.secondaryInsurance?.insurancePlanType,
            insuranceName: fields.metadata?.secondaryInsurance?.insuranceName,
            groupNumber: fields.metadata?.secondaryInsurance?.groupNumber,
            insuranceId: fields.metadata?.secondaryInsurance?.insuranceId,
            otherInsuranceName: fields.metadata?.secondaryInsurance?.otherInsuranceName,
          },
        },
      },
      agent: { email: fields.agentEmail },
    } as LeadAndProvider;

    const wasCreatedSuccessfully = await onboardingService.createLead(lead);

    if (!wasCreatedSuccessfully) {
      setMessage(
        'Referral was not created. It could already have been referred to the Enara program. Please contact an Enara Team representative for further assistance.',
      );
      return;
    }

    handleClearForm();
    setMessage('Referral was created successfully');
    setIsSubmitting(false);
  };

  const optionToChangeEvent = ({
    name,
    value,
  }: {
    name: string;
    value: any;
  }): ChangeEvent<HTMLInputElement | HTMLTextAreaElement> => {
    return { target: { name, value } } as unknown as ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement
    >;
  };

  const genderOptions = Object.entries(Gender).map(([k, v]) => ({ label: k, value: v }));

  // Switching clinics already resets systemSource (see the currentClinic effect below), so
  // a selection that is no longer enabled cannot survive the change.
  const isCohortEnabled = (option: { value: LeadSystemSource }) =>
    isCohortEnabledForClinic(option, currentClinic);

  useEffect(() => {
    const user = AuthService.getLocalProfile();
    setUser(user);

    (async () => {
      const clinics = await CommonService.getClinics();

      setClinics(clinics);
      setCurrentClinic(clinics[0]);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      if (!user || !currentClinic) {
        return;
      }

      // fetchClinicConfig had no caller, so referringDoctorsOptions never left [] and the
      // Referred By list rendered empty at every clinic. It also feeds clinicConfig, which
      // the submit payload reads for patient.clinicName — that was going out undefined too.
      // referral-hub calls the equivalent on load, which is why the list works there.
      await Promise.all([fetchPayers(), fetchClinicConfig()]);

      setFields({
        ...fields,
        clinicId: `${currentClinic.id}`,
        zipCode: currentClinic.zip_code || '',
        agentEmail: user.email,
        systemSource: LeadSystemSource.PartnerReferral,
        source: Source.PartnerReferral,
        // Reset alongside systemSource, which the Cohort selector reads: leaving the
        // derived flag behind would show "Referral" while still submitting a community
        // referral.
        isCommunityReferral: false,
        communityReferralClinicName: '',
      });

      setIsLoading(false);
    })();
  }, [currentClinic, user]);

  useEffect(() => {
    if (canEditBMI || isEmpty(fields.weight) || isEmpty(fields.feet) || isEmpty(fields.inches)) {
      setFields({ ...fields, bmi: null });
      return;
    }

    handleField(
      {
        target: {
          name: 'bmi',
          value: getBMI({
            weight: Number(fields.weight),
            feet: Number(fields.feet),
            inches: Number(fields.inches),
          }),
        },
      },
      true,
    );
  }, [canEditBMI, fields.weight, fields.feet, fields.inches]);

  if (isLoading) {
    return (
      <Box
        flexDirection='column'
        alignItems='center'
        justifyContent='center'
        alignContent='center'
        alignSelf='center'
        justifySelf='center'
        marginBottom='10rem'
        height='100%'
        width='40%'>
        <Typography
          sx={{
            textAlign: 'center',
          }}
          marginBottom={2}>
          Loading...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box flexDirection='column' alignItems='center' className='referral-form-container'>
      <Box className='referral-form-header'>
        <Typography>Referral Form</Typography>

        <Box display='flex' justifyContent='space-between' mt={2} mb={2} gap={2}>
          <Button
            variant='contained'
            color='inherit'
            onClick={handleClearForm}
            disabled={isLoading}
            sx={{
              width: 'auto',
              height: '2em',
              minWidth: '5em',
            }}>
            Clear form
          </Button>
          <Button
            variant='contained'
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{
              width: '1em',
              height: '2em',
              minWidth: '5em',
            }}>
            {isSubmitting ? <CircularProgress size={20} /> : 'Send'}
          </Button>
        </Box>
      </Box>

      <Box display='flex' className='referral-form' gap={3}>
        <Box className='referral-form-section'>
          <Box mb={2}>
            <Typography mb={2}>Patient information</Typography>
          </Box>

          <Box display='flex' gap={2} mb={2} alignItems='center'>
            <Box sx={{ flex: '0 0 auto' }}>
              <Autocomplete
                sx={ROW_SELECT_SX}
                options={clinics.map((clinic) => ({
                  label: clinic.name,
                  value: clinic.id.toString(),
                }))}
                getOptionLabel={(option) => option.label}
                value={
                  clinics
                    .map((clinic) => ({
                      label: clinic.name,
                      value: clinic.id.toString(),
                    }))
                    .find((opt) => opt.value === fields.clinicId) || null
                }
                onChange={(_, option) => {
                  const selectedClinic = clinics.find((c) => c.id.toString() === option?.value);
                  if (selectedClinic) {
                    setCurrentClinic(selectedClinic);
                  }
                  handleField({
                    target: {
                      name: 'clinicId',
                      value: option ? option.value : '',
                    },
                  });
                  handleField(
                    {
                      target: {
                        name: 'zipCode',
                        value: selectedClinic?.zip_code || '',
                      },
                    },
                    true,
                  );
                }}
                onBlur={() => handleBlur('clinicId')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Clinic'
                    required={isFieldRequired('clinicId')}
                    name='clinicId'
                    error={!!error?.clinicId}
                    helperText={
                      !!error?.clinicId && <p key={'error-clinic-id'}>{error?.clinicId}</p>
                    }
                  />
                )}
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />
            </Box>

            <Box sx={{ flex: '0 0 auto' }}>
              <Autocomplete
                sx={ROW_SELECT_SX}
                options={PROGRAM_COHORT_OPTIONS}
                getOptionDisabled={(option) => !isCohortEnabled(option)}
                disableClearable
                autoComplete
                getOptionLabel={(option) => option.label}
                onChange={(_, option) => {
                  const systemSource = (option?.value ??
                    LeadSystemSource.PartnerReferral) as LeadSystemSource;

                  setFields({
                    ...fields,
                    systemSource,
                    // Derived, not driving: the CRM note and sourceCampaign still read
                    // the boolean, but `systemSource` is the source of truth.
                    isCommunityReferral: systemSource === LeadSystemSource.CommunityReferral,
                    communityReferralClinicName:
                      systemSource === LeadSystemSource.CommunityReferral
                        ? fields.communityReferralClinicName
                        : '',
                  });
                }}
                value={
                  PROGRAM_COHORT_OPTIONS.find((o) => o.value === fields.systemSource) ??
                  PROGRAM_COHORT_OPTIONS[0]
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Cohort'
                    name='systemSource'
                    sx={{ margin: 0 }}
                  />
                )}
              />
            </Box>
          </Box>

          {fields.systemSource === LeadSystemSource.CommunityReferral && (
            <Box className='referral-form-column' mb={0}>
              <TextField
                label='Community Referral Clinic Name'
                name='communityReferralClinicName'
                value={fields.communityReferralClinicName}
                onChange={handleField}
                onBlur={() => handleBlur('communityReferralClinicName')}
                placeholder='Enter clinic name'
                helperText='Enter the name of the community clinic'
                fullWidth
              />
            </Box>
          )}

          {/* Patient demographic data */}
          <Box display='flex'>
            <Box display='flex' flexDirection='column' className='referral-form-column'>
              <TextField
                label='Name'
                required={isFieldRequired('firstName')}
                name='firstName'
                value={fields.firstName}
                onChange={handleField}
                onBlur={() => handleBlur('firstName')}
                error={!!error?.firstName}
                helperText={
                  !!error?.firstName && <p key={'error-first-name'}>{error?.firstName}</p>
                }
              />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  label='Date Of Birth'
                  value={fields.dob}
                  onChange={(date: Date | null) => {
                    if (date) {
                      handleField({ target: { name: 'dob', value: formatDateMMDDYYYY(date) } });
                    }
                  }}
                  inputFormat='MM/dd/yyyy'
                  InputProps={{
                    required: isFieldRequired('dob'),
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required={isFieldRequired('dob')}
                      error={!!error?.dob}
                      helperText={error?.dob}
                      name='dob'
                    />
                  )}
                />
              </LocalizationProvider>

              <TextField
                label='Phone'
                required={isFieldRequired('phone')}
                name='phone'
                value={fields.phone ?? ''}
                onChange={handleField}
                onBlur={() => handleBlur('phone')}
                error={!!error?.phone}
                helperText={!!error?.phone && <p key={'error-phone'}>{error?.phone}</p>}
              />

              <FormControl>
                <Autocomplete
                  sx={{ margin: 0 }}
                  options={AVAILABLE_LANGUAGES}
                  onChange={(_, option) => {
                    handleField(
                      optionToChangeEvent({ name: 'preferredLanguage', value: option?.value }),
                    );
                  }}
                  onBlur={() => handleBlur('preferredLanguage')}
                  value={
                    AVAILABLE_LANGUAGES.find((o) => o.value === fields.preferredLanguage) || null
                  }
                  autoComplete
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Preferred Language'
                      name='preferredLanguage'
                      required={isFieldRequired('preferredLanguage')}
                      error={!!error?.preferredLanguage}
                      sx={{
                        margin: 0,
                      }}
                      helperText={
                        !!error?.preferredLanguage && (
                          <p key={'error-preferred-language'}>{error?.preferredLanguage}</p>
                        )
                      }
                    />
                  )}
                />
              </FormControl>
              <TextField
                label='Street'
                required={isFieldRequired('street')}
                name='street'
                value={fields.street ?? ''}
                onChange={handleField}
                onBlur={() => handleBlur('street')}
              />
              <TextField
                label='State'
                required={isFieldRequired('state')}
                name='state'
                value={fields.state ?? ''}
                onChange={handleField}
                onBlur={() => handleBlur('state')}
              />
            </Box>

            <Box display='flex' flexDirection='column' className='referral-form-column'>
              <TextField
                label='Last Name'
                required={isFieldRequired('lastName')}
                name='lastName'
                value={fields.lastName ?? ''}
                onChange={handleField}
                onBlur={() => handleBlur('lastName')}
                error={!!error?.lastName}
                helperText={!!error?.lastName && <p key={'error-last-name'}>{error?.lastName}</p>}
              />
              <FormControl>
                <Autocomplete
                  sx={{ margin: 0 }}
                  options={genderOptions}
                  autoComplete
                  onChange={(_, option) => {
                    handleField(optionToChangeEvent({ name: 'gender', value: option?.value }));
                  }}
                  onBlur={() => handleBlur('gender')}
                  value={genderOptions.find((o) => o.value === fields.gender) || null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Gender'
                      name='gender'
                      error={!!error?.gender}
                      required={isFieldRequired('gender')}
                      sx={{
                        margin: 0,
                      }}
                      helperText={!!error?.gender && <p key={'error-gender'}>{error?.gender}</p>}
                    />
                  )}
                />
              </FormControl>
              <TextField
                label='Email'
                name='email'
                required={isFieldRequired('email')}
                value={fields.email ?? ''}
                onChange={handleField}
                onBlur={() => handleBlur('email')}
                error={!!error?.email}
                helperText={!!error?.email && <p key={'error-email'}>{error?.email}</p>}
              />
              <TextField
                label='Outside MRN'
                name='outsideMRN'
                required={isFieldRequired('outsideMRN')}
                value={fields.outsideMRN ?? ''}
                onChange={handleField}
                onBlur={() => handleBlur('outsideMRN')}
              />
              <TextField
                label='City'
                required={isFieldRequired('city')}
                name='city'
                value={fields.city ?? ''}
                onChange={handleField}
                onBlur={() => handleBlur('city')}
                error={!!error?.city}
                helperText={
                  !!error?.city && (
                    <p key={'error-city'} style={{ margin: 0 }}>
                      {error?.city}
                    </p>
                  )
                }
              />
              <FormControl>
                <Autocomplete
                  sx={{ margin: 0 }}
                  options={referringDoctorsOptions}
                  autoComplete
                  freeSolo
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                  onChange={(_, option) => {
                    const value = typeof option === 'string' ? option : option?.value;

                    handleField(
                      optionToChangeEvent({
                        name: 'referredBy',
                        value,
                      }),
                    );
                  }}
                  onBlur={() => handleBlur('referredBy')}
                  value={
                    referringDoctorsOptions.find((o) => o.value === fields.referredBy) ||
                    fields.referredBy ||
                    null
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Referred By'
                      name='referredBy'
                      required={isFieldRequired('referredBy')}
                      error={!!error?.referredBy}
                      onChange={(value) => {
                        handleField(
                          optionToChangeEvent({
                            name: 'referredBy',
                            value: value.target.value,
                          }),
                        );
                      }}
                      helperText={
                        !!error?.referredBy && <p key='error-referred-by'>{error.referredBy}</p>
                      }
                      sx={{ margin: 0 }}
                    />
                  )}
                />
              </FormControl>
            </Box>
          </Box>
        </Box>

        <Box className='referral-form-section'>
          <Box display='flex' flexDirection='column'>
            <Box mb={2}>
              <Typography mb={2}>Biometric Data</Typography>
            </Box>

            <Box display='flex'>
              <Box display='flex' className='referral-form-column'>
                <TextField
                  disabled={canEditBMI}
                  label='Feet'
                  required={isFieldRequired('feet')}
                  name='feet'
                  type='number'
                  value={fields.feet ?? ''}
                  onChange={handleField}
                  onBlur={() => handleBlur('feet')}
                  error={!!error?.feet}
                  helperText={!!error?.feet && <p key={'error-feet'}>{error?.feet}</p>}
                />
                <TextField
                  disabled={canEditBMI}
                  label='Inches'
                  required={isFieldRequired('inches')}
                  name='inches'
                  type='number'
                  value={fields.inches ?? ''}
                  onChange={handleField}
                  onBlur={() => handleBlur('inches')}
                  error={!!error?.inches}
                  helperText={!!error?.inches && <p key={'error-inches'}>{error?.inches}</p>}
                />
                <TextField
                  disabled={canEditBMI}
                  label='Weight'
                  required={isFieldRequired('weight')}
                  name='weight'
                  type='number'
                  value={fields.weight ?? ''}
                  onChange={handleField}
                  onBlur={() => handleBlur('weight')}
                  error={!!error?.weight}
                  helperText={!!error?.weight && <p key={'error-weight'}>{error?.weight}</p>}
                />
              </Box>
            </Box>

            <Box display='flex'>
              <Box display='flex' className='referral-form-column'>
                <TextField
                  label='BMI'
                  required={isFieldRequired('bmi')}
                  name='bmi'
                  fullWidth
                  disabled={!canEditBMI}
                  value={fields.bmi ?? ''}
                  onChange={handleField}
                  onBlur={() => handleBlur('bmi')}
                  error={!!error?.bmi}
                  helperText={!!error?.bmi && <p key={'error-weight'}>{error?.bmi}</p>}
                />
              </Box>
              <FormGroup>
                <FormControlLabel
                  label='Edit BMI'
                  control={<Switch onChange={(_e) => handleToggleBmiEdition()} />}
                />
              </FormGroup>
            </Box>
          </Box>

          <Box display='flex' flexDirection='column'>
            <Box>
              <Typography mb={2}>Coverage Information</Typography>
            </Box>

            <Box display='flex'>
              <Box display='flex' flexDirection='column' className='referral-form-column'>
                <FormControl>
                  <Autocomplete
                    sx={{ margin: 0 }}
                    options={AVAILABLE_PLAN_TYPES}
                    autoComplete
                    onChange={(_, option) =>
                      handleField(
                        optionToChangeEvent({ name: 'insurancePlanType', value: option?.value }),
                      )
                    }
                    onBlur={() => handleBlur('insurancePlanType')}
                    value={
                      AVAILABLE_PLAN_TYPES.find((o) => o.value === fields.insurancePlanType) || null
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Insurance Plan Type'
                        required={isFieldRequired('insurancePlanType')}
                        error={!!error?.insurancePlanType}
                        sx={{
                          margin: 0,
                        }}
                        helperText={
                          !!error?.insurancePlanType && (
                            <p key={'error-insurance-plan-type'}>{error?.insurancePlanType}</p>
                          )
                        }
                      />
                    )}
                  />
                </FormControl>
                <TextField
                  label='Group Number'
                  name='groupNumber'
                  required={isFieldRequired('groupNumber')}
                  value={fields.groupNumber ?? ''}
                  onChange={handleField}
                  onBlur={() => handleBlur('groupNumber')}
                />
                <FormControl>
                  <Autocomplete
                    sx={{ margin: 0 }}
                    options={payerOptions}
                    autoSelect
                    autoComplete
                    onChange={(_, option) =>
                      handleField(
                        optionToChangeEvent({ name: 'insuranceName', value: option?.value }),
                      )
                    }
                    onBlur={() => handleBlur('insuranceName')}
                    value={payerOptions.find((o) => o.value === fields.insuranceName) || null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name='insuranceName'
                        label='Insurance Name'
                        required={isFieldRequired('insuranceName')}
                        error={!!error?.insuranceName}
                        sx={{
                          margin: 0,
                        }}
                        helperText={
                          !!error?.insuranceName && (
                            <p key={'error-insurance-name'}>{error?.insuranceName}</p>
                          )
                        }
                      />
                    )}
                  />
                </FormControl>
              </Box>
              <Box display='flex' flexDirection='column' className='referral-form-column'>
                <TextField
                  label='Insurance ID'
                  required={isFieldRequired('insuranceId')}
                  name='insuranceId'
                  value={fields.insuranceId ?? ''}
                  onChange={handleField}
                  onBlur={() => handleBlur('insuranceId')}
                  error={!!error?.insuranceId}
                  helperText={
                    !!error?.insuranceId && <p key={'error-insurance-id'}>{error?.insuranceId}</p>
                  }
                />
                <TextField
                  label='Other Insurance Name'
                  name='otherInsuranceName'
                  required={isFieldRequired('otherInsuranceName')}
                  value={fields.otherInsuranceName ?? ''}
                  onChange={handleField}
                  onBlur={() => handleBlur('otherInsuranceName')}
                />
              </Box>
            </Box>
          </Box>

          <Box display='flex' flexDirection='column'>
            <Box>
              <Typography mb={2}>Secondary Insurance</Typography>
            </Box>

            <Box display='flex'>
              <Box display='flex' flexDirection='column' className='referral-form-column'>
                <FormControl>
                  <Autocomplete
                    sx={{ margin: 0 }}
                    options={AVAILABLE_PLAN_TYPES}
                    autoComplete
                    onChange={(_, option) =>
                      handleField(
                        optionToChangeEvent({
                          name: 'metadata.secondaryInsurance.insurancePlanType',
                          value: option?.value,
                        }),
                      )
                    }
                    value={
                      AVAILABLE_PLAN_TYPES.find(
                        (o) => o.value === fields.metadata?.secondaryInsurance?.insurancePlanType,
                      ) || null
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Insurance Plan Type'
                        sx={{
                          margin: 0,
                        }}
                      />
                    )}
                  />
                </FormControl>
                <TextField
                  label='Group Number'
                  name='metadata.secondaryInsurance.groupNumber'
                  value={fields.metadata?.secondaryInsurance?.groupNumber ?? ''}
                  onChange={handleField}
                  onBlur={() => handleBlur('groupNumber')}
                />
                <FormControl>
                  <Autocomplete
                    sx={{ margin: 0 }}
                    options={payerOptions}
                    autoSelect
                    autoComplete
                    onChange={(_, option) =>
                      handleField(
                        optionToChangeEvent({
                          name: 'metadata.secondaryInsurance.insuranceName',
                          value: option?.value,
                        }),
                      )
                    }
                    value={
                      payerOptions.find(
                        (o) => o.value === fields.metadata?.secondaryInsurance?.insuranceName,
                      ) || null
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name='metadata.secondaryInsurance.insuranceName'
                        label='Insurance Name'
                        sx={{
                          margin: 0,
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Box>
              <Box display='flex' flexDirection='column' className='referral-form-column'>
                <TextField
                  label='Insurance ID'
                  name='metadata.secondaryInsurance.insuranceId'
                  value={fields.metadata?.secondaryInsurance?.insuranceId ?? ''}
                  onChange={handleField}
                  onBlur={() => handleBlur('insuranceId')}
                />
                <TextField
                  label='Other Insurance Name'
                  name='metadata.secondaryInsurance.otherInsuranceName'
                  value={fields.metadata?.secondaryInsurance?.otherInsuranceName ?? ''}
                  onChange={handleField}
                  onBlur={() => handleBlur('otherInsuranceName')}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={!!message}
        autoHideDuration={10000}
        onClose={() => setMessage('')}
        message={message}
        ContentProps={{
          style: { marginTop: '50px' },
        }}
      />
    </Box>
  );
};

export default ReferralForm;
